import styles from "./ViewUser.module.css";
import { getPageFromName } from "../../route_utils";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api_endpoints from "../../api";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import { Button, Chip } from "@mui/material";
import dayjs from "dayjs";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";

const ViewUser = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useCustomTranslation("Utilisateurs");

  const userId = searchParams.get("user_id") || state?.user_id;

  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setLoadingError(true);
      return;
    }

    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    setLoading(true);
    setLoadingError(false);

    try {
      const response = await api_endpoints.getUserById(userId);

      if (response.status === 200) {
        setUser(response.data);
      } else {
        setLoadingError(true);
      }
    } catch (error) {
      console.error(error);
      setLoadingError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`${getPageFromName("Paramètres utilisateur").path}?user_id=${userId}`, {
      state: { user_id: userId },
    });
  };

  return (
    <LoadingContainer loading={loading} error={loadingError}>
      <div className={styles.main_container}>
        <div className="flex justify-end">
          <Button variant="contained" onClick={handleEdit}>
            {t("view_user_edit_button", { defaultValue: "Modifier" })}
          </Button>
        </div>

        <div className={styles.user_details_container}>
          <div className={styles.section_title}>{t("view_user_section_general", { defaultValue: "Informations générales" })}</div>

          <div className={styles.grid}>
            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_first_name", { defaultValue: "Prénom" })}</div>
              <div>{user?.first_name || "-"}</div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_last_name", { defaultValue: "Nom" })}</div>
              <div>{user?.last_name || "-"}</div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_email", { defaultValue: "Courriel" })}</div>
              <div>{user?.email || "-"}</div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_phone", { defaultValue: "Téléphone" })}</div>
              <div>{user?.phone || "-"}</div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_location", { defaultValue: "Emplacement" })}</div>
              <div>{user?.location_name || "-"}</div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_kiosk_pin", { defaultValue: "NIP kiosk" })}</div>
              <div>{user?.has_kiosk_code
                ? t("view_user_pin_configured", { defaultValue: "Configuré" })
                : t("view_user_pin_not_configured", { defaultValue: "Non configuré" })}</div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_created_at", { defaultValue: "Date de création" })}</div>
              <div>
                {user?.created_at
                  ? dayjs(user.created_at).format("YYYY-MM-DD")
                  : "-"}
              </div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_status", { defaultValue: "Statut" })}</div>
              <div>
                <Chip
                  label={user?.is_active
                    ? t("view_user_active", { defaultValue: "Actif" })
                    : t("view_user_inactive", { defaultValue: "Inactif" })}
                  color={user?.is_active ? "success" : "error"}
                  variant="filled"
                />
              </div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_verified", { defaultValue: "Vérifié" })}</div>
              <div>
                <Chip
                  label={user?.is_verified
                    ? t("view_user_yes", { defaultValue: "Oui" })
                    : t("view_user_no", { defaultValue: "Non" })}
                  color={user?.is_verified ? "success" : "error"}
                  variant="filled"
                />
              </div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_owner", { defaultValue: "Propriétaire" })}</div>
              <div>
                <Chip
                  label={user?.is_owner
                    ? t("view_user_yes", { defaultValue: "Oui" })
                    : t("view_user_no", { defaultValue: "Non" })}
                  color={user?.is_owner ? "success" : "default"}
                  variant="filled"
                />
              </div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_super_user", { defaultValue: "Super utilisateur" })}</div>
              <div>
                <Chip
                  label={user?.is_super_user
                    ? t("view_user_yes", { defaultValue: "Oui" })
                    : t("view_user_no", { defaultValue: "Non" })}
                  color={user?.is_super_user ? "success" : "default"}
                  variant="filled"
                />
              </div>
            </div>

            <div className={styles.field_block}>
              <div className={styles.field_label}>{t("view_user_deleted", { defaultValue: "Supprimé" })}</div>
              <div>
                <Chip
                  label={user?.is_deleted
                    ? t("view_user_yes", { defaultValue: "Oui" })
                    : t("view_user_no", { defaultValue: "Non" })}
                  color={user?.is_deleted ? "error" : "success"}
                  variant="filled"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </LoadingContainer>
  );
};

export default ViewUser;